import Vue from 'vue';
import Router from 'vue-router';
import URLS from './URLS';
import AuthService from './services/AuthService';
import AnalyticService from './services/AnalyticService';
import RestX from 'highered-utils/restx';
import UniversityService from './services/UniversityService';
import DialogService from './services/DialogService';

// Configure feature toggles
import FeatureToggles from 'highered-utils/feature-toggle';
import buildConfig from './config/config.json';
FeatureToggles.useToggles(buildConfig['toggles']);
Vue.use(FeatureToggles);

import i18n from '~/i18n';

Vue.use(Router);

// ROUTE TOGGLES
export const SHOW_JOBS = FeatureToggles.toggle('pages.findJobs');
export const SHOW_COMPANIES = FeatureToggles.toggle('pages.findCompanies');
export const HAS_LOGIN = FeatureToggles.toggle('logIn.logIn');

export const HAS_MY_PAGE = HAS_LOGIN && FeatureToggles.toggle('pages.myPage');
export const HAS_ASSESSMENTS = HAS_LOGIN && FeatureToggles.toggle('pages.assessments');
export const HAS_MASTERCLASSES = HAS_LOGIN && FeatureToggles.toggle('pages.masterClasses');
export const HAS_CALENDAR = HAS_LOGIN && FeatureToggles.toggle('pages.calendar');
export const HAS_INTERNAL_RESOURCES = HAS_LOGIN && FeatureToggles.toggle('pages.internalResources');
export const HAS_COUNTRY_GUIDES = HAS_LOGIN && FeatureToggles.toggle('pages.countryGuides');
export const HAS_HAIGUI_LINK = HAS_LOGIN && FeatureToggles.toggle('pages.haiguiLink');

export const PROMOTED_RESOURCE = FeatureToggles.toggle('promotedResource');

export const SHOW_PROFILE_BANNER = HAS_LOGIN && FeatureToggles.toggle('logIn.profileBanner');
export const PROFILE_MODULE = HAS_LOGIN && FeatureToggles.toggle('logIn.profileModule');
export const FEATURE_COMPANIES_MODULE = FeatureToggles.toggle('companies.featuredModule');
export const FEATURE_POSITIONS_MODULE = FeatureToggles.toggle('positions.featuredModule');
export const FEATURE_POSITIONS_IN_LIST = FeatureToggles.toggle('positions.featured');

export const FORCE_VERIFIED_APPLICATION_CLICK =
    HAS_LOGIN && FeatureToggles.toggle('verify.jobApply');

export const VERIFY_MODULE = HAS_LOGIN && FeatureToggles.toggle('verify.verifyModule');

export const REQUIRE_VERIFICATION_BADGE_PROMPT =
    HAS_LOGIN && FeatureToggles.toggle('verify.badgePrompt');

//Masterclass-related verification requirements
const VERIFY_MASTERCLASS =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.resources');

//Internal-resource-related verification requirements
const VERIFY_INTERNAL_RESOURCES_OVERVIEW =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.internalResources.overview');

const VERIFY_INTERNAL_RESOURCES_FOLDER =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.internalResources.folder');

const VERIFY_INTERNAL_RESOURCES_PARENT =
    VERIFY_INTERNAL_RESOURCES_OVERVIEW &&
    VERIFY_INTERNAL_RESOURCES_FOLDER &&
    FeatureToggles.toggle('verify.requireVerification.internalResources');

//Calendar-related verification requirements
const VERIFY_CALENDAR_OVERVIEW =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.overview');

const VERIFY_CALENDAR_BOOK =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.appointmentList');

const VERIFY_CALENDAR_APPOINTMENT_BOOK =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.appointmentBook');

const VERIFY_CALENDAR_PARENT =
    VERIFY_CALENDAR_OVERVIEW &&
    VERIFY_CALENDAR_BOOK &&
    VERIFY_CALENDAR_APPOINTMENT_BOOK &&
    FeatureToggles.toggle('verify.requireVerification.calendar');

// Full parent verification requirement
export const REQUIRE_VERIFICATION =
    VERIFY_INTERNAL_RESOURCES_PARENT &&
    VERIFY_CALENDAR_PARENT &&
    VERIFY_MASTERCLASS &&
    FeatureToggles.toggle('verify.requireVerification');

export const HAS_VFAIRS = true; //!FeatureToggles.toggle('beta.vfairs');

let routes = [
    {
        path: URLS.mypage,
        name: 'mypage',
        component: () => import('./views/mypage/MyPage'),
        meta: { requiresAuth: true },
        toggle: HAS_MY_PAGE,
    },
    {
        path: URLS.findjobs,
        name: 'jobs',
        component: () => import('./views/findjobs/FindJobs'),
    },
    {
        path: URLS.jobDetail.concat('/:id'),
        name: 'jobdetail',
        component: () => import('./views/jobdetail/JobDetail'),
    },
    {
        path: URLS.resources,
        name: 'resources',
        component: () => import('./views/resources/Resources'),
        meta: {
            requiresAuth: true,
            requiresVerification: VERIFY_MASTERCLASS,
        },
        toggle: HAS_MASTERCLASSES,
    },
    {
        path: URLS.internalResources,
        name: 'internalresources',
        component: () => import('./views/resources_internal/overview'),
        meta: {
            requiresAuth: true,
            requiresVerification: VERIFY_INTERNAL_RESOURCES_OVERVIEW,
        },
        toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_INTERNAL_RESOURCES_OVERVIEW,
    },
    {
        path: URLS.internalResources.concat('/:id'),
        name: 'internalresources-folder',
        component: () => import('./views/resources_internal/folder'),
        meta: {
            requiresAuth: true,
            requiresVerification: VERIFY_INTERNAL_RESOURCES_FOLDER,
        },
        toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_INTERNAL_RESOURCES_FOLDER,
    },
    {
        path: URLS.calendar,
        name: 'calendar-overview',
        component: () => import('./views/calendar/overview'),
        meta: {
            requiresAuth: true,
            requiresVerification: VERIFY_CALENDAR_OVERVIEW,
        },
        toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_OVERVIEW,
    },
    {
        path: URLS.calendar.concat(URLS.book),
        name: 'calendar-appointments-list',
        component: () => import('./views/calendar/appointment_list'),
        meta: {
            requiresAuth: true,
            requiresVerification: VERIFY_CALENDAR_BOOK,
        },
        toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_BOOK,
    },
    {
        path: URLS.calendar.concat(URLS.book).concat('/:id'),
        name: 'calendar-appointments-book',
        component: () => import('./views/calendar/appointment_book'),
        meta: {
            requiresAuth: true,
            requiresVerification: VERIFY_CALENDAR_APPOINTMENT_BOOK,
        },
        toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_APPOINTMENT_BOOK,
    },
    {
        path: URLS.findcompanies,
        name: 'companies',
        component: () => import('./views/findcompanies/FindCompanies'),
    },
    {
        path: URLS.findcompanies.concat('/:id'),
        name: 'companydetail',
        component: () => import('./views/companydetail/CompanyDetail'),
    },
    {
        path: URLS.spotlight,
        name: 'spotlight',
        component: () => import('./views/spotlight/Spotlight'),
        meta: { requiresAuth: true },
        toggle: HAS_MY_PAGE,
    },
    {
        path: URLS.favorites,
        name: 'favorites',
        component: () => import('./views/favorites/Favorites'),
        meta: { requiresAuth: true },
        toggle: HAS_MY_PAGE,
    },
    {
        path: URLS.tests,
        component: () => import('./views/tests/Tests'),
        meta: { requiresAuth: true },
        children: [
            {
                path: '/',
                name: 'tests-dashboard',
                component: () => import('./views/tests/TestsDashboard'),
                meta: { requiresAuth: true },
            },
            {
                path: URLS.cute,
                name: 'tests-cute',
                component: () => import('./views/test_cute/Cute'),
                meta: { requiresAuth: true },
            },
        ],
        toggle: HAS_ASSESSMENTS,
    },
    {
        path: URLS.emailVerification,
        name: 'emailVerification',
        component: () => import('./views/emailverification/EmailVerification'),
    },
    {
        path: URLS.recoverUser,
        name: 'recoverUser',
        component: () => import('./views/recoveruser/RecoverUser'),
    },
    {
        path: URLS.recoverPassword,
        name: 'recoverPassword',
        component: () => import('./views/recoverpassword/RecoverPassword'),
    },
    {
        path: URLS.redirect, // Is for handling redirection-events, for example oauth-flow
        name: 'redirect',
        component: () => import('./views/redirect/Redirect'),
    },
    {
        path: URLS.aboutGPTW,
        name: 'aboutGPTW',
        component: () => import('./views/aboutGPTW/AboutGPTW'),
    },
    {
        path: URLS.activateUser,
        name: 'activateUser',
        component: () => import('./views/activateuser/ActivateUser'),
    },
    {
        path: URLS.vfair,
        name: 'vfairs',
        component: () => import('./views/vfairs/list/FindVFairs'),
        meta: {
            requiresAuth: true,
            requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
        },
    },
    {
        path: URLS.vfair.concat('/:id'),
        name: 'vfairdetails',
        component: () => import('./views/vfairs/details/VFairDetails'),
        meta: {
            requiresAuth: true,
            requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
        },
    },
    {
        path: '*',
        name: 'pagenotfound',
        component: () => import('./views/pagenotfound/PageNotFound'),
    },
];

// Remove routes that has been toggled off
routes = routes.filter(r => (r.toggle instanceof Boolean ? r.toggle : true));

const router = new Router({
    base: '/', // process.env.BASE_URL,
    mode: 'history',
    scrollBehavior(to, from, savedPosition) {
        // Check
        return { x: 0, y: 0 };
    },
    routes: routes,
});

router.beforeEach((to, from, next) => {
    const logInModule = RestX.getInterfaces().logInModule;
    logInModule.resetDisabledPopup();
    // Check if user is trying to leave spotlight-page with unsaved changes
    if (from.path === URLS.spotlight && AuthService.isLoggedIn()) {
        if (RestX.getInterfaces().unsavedChanges.hasUnsavedChanges()) {
            RestX.getInterfaces().unsavedChanges.setRedirect(to.path);
            RestX.getInterfaces().unsavedChanges.toggleOpen();
        } else {
            next();
        }
    } else if (to.matched.some(record => record.meta.requiresAuth)) {
        // Check if authenticated (there is a token)
        if (!AuthService.isLoggedIn()) {
            RestX.getInterfaces().logInModule.toggleOpen();
            RestX.getInterfaces().logInModule.setRedirect(to.path);
            RestX.getInterfaces().logInModule.setThen(() => next());
        } else {
            if (
                to.matched.some(
                    record =>
                        record.meta.requiresVerification &&
                        FeatureToggles.toggle('verify.requireVerification')
                )
            ) {
                const currentUniDomain = UniversityService.getCurrentDomain();
                RestX.getInterfaces()
                    .university.connection.get({})
                    .then(it => {
                        let currentUni = it.data.find(
                            c => (c.school.subDomain || '').toLowerCase() === currentUniDomain
                        );
                        if (currentUni) {
                            // User either is verified at this school or has a pending verification
                            if (currentUni.isVerified) {
                                next();
                            } else {
                                DialogService.openUniVerifyDialog({
                                    payload: {
                                        checkConnections: true,
                                        helperText: i18n.t(
                                            'general.dialogs.helperTexts.checkHasPendingAction'
                                        ),
                                    },
                                    then: payload => {
                                        if (payload instanceof Object && payload.isVerified) {
                                            next();
                                        }
                                    },
                                });
                            }
                        } else {
                            // User needs to verify to continue
                            DialogService.openUniVerifyDialog({
                                payload: {
                                    checkConnections: true,
                                    // TODO: FIX DIS TEXT
                                    helperText: i18n.t(
                                        'general.dialogs.helperTexts.verifySchoolConnection'
                                    ),
                                },
                                then: payload => {
                                    if (payload instanceof Object && payload.isVerified) {
                                        next();
                                    }
                                },
                            });
                        }
                    });
            } else {
                next();
            }
        }
    } else {
        next(); // Always have to call next()
    }
});

router.afterEach(to => {
    // Log pageviews. Pages that are just an redirect from external token-providers
    // like /redirect will not be logged. Same with /recover and /recover-password
    // because they contain sensitive data in the url-query.
    if (
        !to.path.startsWith(URLS.redirect) &&
        !to.path.startsWith(URLS.recoverUser) &&
        !to.path.startsWith(URLS.recoverPassword)
    ) {
        AnalyticService.logPageview(to.path);
    }

    RestX.getInterfaces().university.connection.get({ forceUpdate: true });

    const logInModule = RestX.getInterfaces().logInModule;
    if (logInModule.shouldOpenOnNewPage()) {
        logInModule.setHelperText(i18n.t('general.dialogs.helperTexts.logInToAccessMoreFeatures'));
        logInModule.setOpen(true);
        logInModule.setShouldOpenOnNewPage(false);
    }
});

export default router;
